import type { BBox, Building, LatLng, OrientedRect, Point } from './types';

export const toRad = (d: number): number => (d * Math.PI) / 180;
export const toDeg = (r: number): number => (r * 180) / Math.PI;

export function rotP(p: Point, ang: number): Point {
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}
export function subP(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}
export function addP(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}
export function scaleVec(v: Point, s: number): Point {
  return { x: v.x * s, y: v.y * s };
}
export function normalizeVec(v: Point): Point {
  const len = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / len, y: v.y / len };
}

export function polygonArea(pts: Point[]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    s += p1.x * p2.y - p2.x * p1.y;
  }
  return s / 2;
}

export function polygonCentroid(pts: Point[]): Point {
  const a = polygonArea(pts);
  if (Math.abs(a) < 1e-9) {
    let sx = 0;
    let sy = 0;
    pts.forEach((p) => {
      sx += p.x;
      sy += p.y;
    });
    return { x: sx / pts.length, y: sy / pts.length };
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    const cr = p1.x * p2.y - p2.x * p1.y;
    cx += (p1.x + p2.x) * cr;
    cy += (p1.y + p2.y) * cr;
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

export function pointInPolygon(pt: Point, pts: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x;
    const yi = pts[i].y;
    const xj = pts[j].x;
    const yj = pts[j].y;
    const intersect = yi > pt.y !== yj > pt.y && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// The site's "main axis" angle, found by a length-weighted majority vote over
// each edge's direction (so short notches/jogs don't skew the result away
// from the site's dominant orientation).
export function computeSiteMainAxisAngleRad(points: Point[]): number {
  const n = points.length;
  if (n < 2) return 0;
  const DIR_BIN_DEG = 1;
  const bins = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    let ang = Math.atan2(dy, dx) % Math.PI;
    if (ang < 0) ang += Math.PI;
    const key = (Math.round(toDeg(ang) / DIR_BIN_DEG) * DIR_BIN_DEG) % 180;
    bins.set(key, (bins.get(key) || 0) + len);
  }
  if (bins.size === 0) return 0;
  let bestKey = 0;
  let bestLen = -1;
  for (const [k, v] of bins) {
    if (v > bestLen) {
      bestLen = v;
      bestKey = k;
    }
  }
  return toRad(bestKey);
}

export function convexHull(points: Point[]): Point[] {
  const pts = points.slice().sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  if (pts.length <= 2) return pts;
  const cross = (o: Point, a: Point, b: Point) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: Point[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function bbox(pts: Point[]): BBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  pts.forEach((p) => {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  });
  return { minX, minY, maxX, maxY };
}

export function distToSegment(pt: Point, a: Point, b: Point): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 > 0 ? ((pt.x - a.x) * abx + (pt.y - a.y) * aby) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + abx * t;
  const py = a.y + aby * t;
  return Math.hypot(pt.x - px, pt.y - py);
}

export function projectPointOnSegment(pt: Point, a: Point, b: Point): { t: number; point: Point } {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 > 0 ? ((pt.x - a.x) * abx + (pt.y - a.y) * aby) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return { t, point: { x: a.x + abx * t, y: a.y + aby * t } };
}

export function distToPolygon(pt: Point, pts: Point[]): number {
  let min = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const d = distToSegment(pt, pts[i], pts[(i + 1) % pts.length]);
    if (d < min) min = d;
  }
  return min;
}

export function insideBuildingWithMargin(pt: Point, b: OrientedRect, margin: number): boolean {
  const lp = rotP(subP(pt, b.center), -b.angle);
  return Math.abs(lp.x) <= b.width / 2 + margin && Math.abs(lp.y) <= b.depth / 2 + margin;
}

/** Outward-facing normal of edge p1->p2, oriented away from `centroid`. Shared by the slope-restriction check and the parking engine's road/wall placement. */
export function edgeOutwardNormal(p1: Point, p2: Point, centroid: Point): Point {
  const d = subP(p2, p1);
  const n = normalizeVec({ x: d.y, y: -d.x });
  const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  const toMid = subP(mid, centroid);
  return n.x * toMid.x + n.y * toMid.y >= 0 ? n : { x: -n.x, y: -n.y };
}

// Latitude/longitude <-> local meter coordinates (flat-earth approximation,
// valid at the scale of a single building site).
const M_PER_DEG_LAT = 111320;
export function mPerDegLng(lat: number): number {
  return M_PER_DEG_LAT * Math.cos(toRad(lat));
}
export function latLngToLocal(latlng: LatLng, origin: LatLng): Point {
  const dLat = latlng.lat - origin.lat;
  const dLng = latlng.lng - origin.lng;
  return { x: dLng * mPerDegLng(origin.lat), y: dLat * M_PER_DEG_LAT };
}
export function localToLatLng(pt: Point, origin: LatLng): LatLng {
  return {
    lat: origin.lat + pt.y / M_PER_DEG_LAT,
    lng: origin.lng + pt.x / mPerDegLng(origin.lat),
  };
}

export function orientedRectCorners(r: OrientedRect): Point[] {
  const w = r.width / 2;
  const d = r.depth / 2;
  const local = [
    { x: -w, y: -d },
    { x: w, y: -d },
    { x: w, y: d },
    { x: -w, y: d },
  ];
  return local.map((p) => addP(rotP(p, r.angle), r.center));
}
export function buildingCornersLm(b: OrientedRect): Point[] {
  return orientedRectCorners(b);
}

export const DEFAULT_FLOOR_HEIGHT_M = 3.0;
export function getBuildingHeight(b: Building): number {
  return b.floors * (b.floorHeightM != null ? b.floorHeightM : DEFAULT_FLOOR_HEIGHT_M);
}
